import React from "react";
import { Divider, Panel, Tb, Td, Th } from "@/components/portal/crm/kit";
import { Code, mono } from "./ConfirmDialog";

const FIELDS = [
  ["applicant_name", "Required", "The client's full name"],
  ["finance_type", "Required", "e.g. Business Loan, Asset Finance, Invoice Finance"],
  ["amount_gbp", "Required", "How much they need, in pounds"],
  ["applicant_email / applicant_phone", "One of", "So the desk can reach the client"],
  ["external_id", "Optional", "Your own id — you can look a case up by it later"],
  ["business_name, company_number, industry_sector", "Optional", "The company"],
  ["annual_revenue_gbp, trading_years, term_months", "Optional", "The numbers a desk asks for first"],
  ["loan_purpose, notes", "Optional", "Anything the desk should know"],
];

const submit = (base) => `curl -X POST ${base}/leads \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_id": "crm-10432",
    "applicant_name": "Sam Patel",
    "applicant_email": "sam@patelbuild.co.uk",
    "applicant_phone": "07700 900123",
    "business_name": "Patel Build Ltd",
    "finance_type": "Business Loan",
    "amount_gbp": 85000
  }'`;

const PAYLOAD = `{
  "id": "evt_4f1c…",
  "type": "case.stage_changed",
  "previous_stage": "sent",
  "case": {
    "id": "…", "reference": "CX-A05345", "external_id": "crm-10432",
    "stage": "in_review", "stage_label": "Under assessment", "stage_note": "a lender is assessing it",
    "right_now": { "key": "callback", "label": "Call back booked", "desk": "Desk A",
                   "waiting_on": "The client — to take the booked call",
                   "next_step": "Speak to the client at the booked time", "callback_at": "2026-11-03T14:00:00Z" },
    "amount_gbp": 85000, "possible_duplicate": false, "offers": []
  }
}`;

const VERIFY = `# Header:  X-Connexis-Signature: t=1761730000,v1=<hex>
signed = f"{t}.{raw_body}"
expected = hmac.new(secret.encode(), signed.encode(), hashlib.sha256).hexdigest()
ok = hmac.compare_digest(expected, v1)`;

/** Everything a developer needs on one page — no separate docs site to keep in step. */
export const ApiGuide = ({ info }) => {
  const base = info?.api_base || "";
  return (
    <Panel title="Developer guide" testId="api-guide-panel" sub="Hand this to whoever looks after your system.">
      <div style={{ display: "grid", gap: 18 }}>
        <Step n="1" t="Send a case" s={<>POST to <span style={mono} data-testid="api-guide-endpoint">{base}/leads</span>. We answer 201 with the case and its reference.</>} />
        <Code testId="api-guide-curl">{submit(base)}</Code>
        <Tb testId="api-guide-fields" head={<><Th>Field</Th><Th>Needed</Th><Th>What it is</Th></>}>
          {FIELDS.map(([f, n, w]) => (
            <tr key={f}><Td><span style={mono}>{f}</span></Td><Td>{n}</Td><Td>{w}</Td></tr>
          ))}
        </Tb>
        <Step n="2" t="A client you've sent before" s="Same email or same phone as a case already in the Hub? We still take it — it's flagged so the desk checks it, and possible_duplicate comes back true when it's one of yours." />
        <Step n="3" t="Check a stage any time" s={<>GET <span style={mono}>{base}/leads/{"{id | reference | external_id}"}</span>, or list them with GET <span style={mono}>{base}/leads?updated_since=…</span></>} />
        <Step n="4" t="Get every move pushed to you" s="Save your address above. Each move is a signed JSON POST — case.stage_changed when the stage moves, case.updated when a lender posts a Right now update. Answer with any 2xx. If you don't, we try again after 30 seconds and after 5 minutes." />
        <Code testId="api-guide-payload">{PAYLOAD}</Code>
        <Code testId="api-guide-verify">{VERIFY}</Code>
        <Divider />
        <div>
          <div className="rail-t" style={{ marginBottom: 8 }}>Stages you'll see</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="api-guide-stages">
            {(info?.stages || []).map((s) => (
              <span key={s.key} className="copyrow" style={{ ...mono, padding: "6px 10px" }} title={`${s.label} · ${s.note}`}>{s.key}</span>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
};

const Step = ({ n, t, s }) => (
  <div style={{ display: "flex", gap: 14 }}>
    <span style={{ flex: "none", width: 26, height: 26, borderRadius: 8, display: "grid", placeItems: "center", fontWeight: 800,
                   fontSize: 12.5, background: "var(--accent-soft)", color: "var(--accent-text)" }}>{n}</span>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{t}</div>
      <div style={{ fontSize: 13, color: "var(--mut)", marginTop: 3, lineHeight: 1.6, overflowWrap: "anywhere" }}>{s}</div>
    </div>
  </div>
);
